import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectUser } from 'src/app/viewbid/state/app-selectors';
import { User } from 'viewbid-shared-lib';
import { filter, map, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/viewbid/state/app-state';
import {
    AbstractControl,
    AsyncValidator,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { VbSnackbarService } from '@viewbid/ui';
import { TranslateService } from '@ngx-translate/core';
import { phoneNumberValidator } from '../../validators/phone-number-validator';

import {
    GetCurrentUser200Response,
    ProfilesService,
    PutUserInformationRequest,
    UsersService,
} from '@viewbid/ts-angular-client';
import { ProfileService } from 'src/app/viewbid/shared/services/profile.service';
import { usernameExistsValidatorFactory } from '../../validators/username-exists-validator';
import { Subject, timer } from 'rxjs';
import { UsernameService } from '../../services/username.service';
import { prependCanadaAndUSCountryCode } from 'src/app/viewbid/utils/prepend-country-code';
import { DeviceCheckService } from 'src/app/viewbid/shared/services/device-check.service';

@Component({
    selector: 'vb-user-information',
    templateUrl: './user-information.component.html',
    styleUrls: ['./user-information.component.scss'],
})
export class UserInformationComponent implements OnInit, OnDestroy {
    public currentUser!: User;
    public userEmail: string = '';
    public userInformationForm!: FormGroup;
    /** Save confirm (vb-dialog) — replaces the legacy ngx-bootstrap
     *  ConfirmationDialogComponent for this flow. */
    confirmOpen: boolean = false;
    isEditMode: boolean = false;
    private destroy$ = new Subject<void>();
    isLoading: boolean = false;
    verifyPhoneNumber: boolean = false;
    isTelephoneVerified: boolean = false;
    sendOTP: boolean = false;
    public isMobile = false;

    constructor(
        private store: Store<AppState>,
        private _formBuilder: FormBuilder,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
        private profilesService: ProfilesService,
        private profileService: ProfileService,
        private usernameService: UsernameService,
        private userService: UsersService,
        private deviceCheckService: DeviceCheckService,
    ) {}

    ngOnInit(): void {
        // Get user data from API
        this.userService
            .getCurrentUser()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: GetCurrentUser200Response) => {
                    if (response.data) {
                        this.userEmail = response.data.email?.email ?? '';
                        this.userInformationForm
                            ?.get('email')
                            ?.patchValue(
                                this.userEmail || this.currentUser?.email || '',
                            );
                        this.isTelephoneVerified =
                            response.data.telephone?.isVerified ?? false;
                    }
                },
            });

        this.store
            .select<User | null>(selectUser)
            .pipe(
                filter((user: User | null): user is User => user !== null),
                map((user: User) => user || ''),
                takeUntil(this.destroy$), // Automatically unsubscribe on destroy$
            )
            .subscribe((user: User) => {
                this.currentUser = user;
                if (this.userInformationForm && this.isEditMode) {
                    const usernameValidator: AsyncValidator =
                        usernameExistsValidatorFactory(
                            this.usernameService,
                            user.username,
                        );
                    this.userInformationForm
                        .get('username')
                        ?.setAsyncValidators((control: AbstractControl) =>
                            usernameValidator.validate(control),
                        );
                    this.userInformationForm.patchValue({
                        email: this.userEmail || user.email,
                        username: user.username,
                    });
                    return;
                }
                this.userInformationForm = this._formBuilder.group({
                    email: new FormControl(null, [
                        Validators.required,
                        Validators.maxLength(50),
                        Validators.email,
                        Validators.pattern(
                            '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$',
                        ),
                    ]),
                    username: [
                        '',
                        [
                            Validators.required,
                            Validators.maxLength(30),
                            Validators.pattern(/^[a-zA-Z0-9]*$/),
                        ],
                        [
                            usernameExistsValidatorFactory(
                                this.usernameService,
                                this.currentUser.username,
                            ),
                        ],
                    ],
                    firstname: new FormControl(null, [
                        Validators.required,
                        Validators.maxLength(25),
                        Validators.pattern(/^[A-Za-z]+( [A-Za-z]+)*$/),
                    ]),
                    lastname: new FormControl(null, [
                        Validators.required,
                        Validators.maxLength(25),
                        Validators.pattern(/^[A-Za-z]+( [A-Za-z]+)*$/),
                    ]),
                    phoneNumber: new FormControl(null, [
                        Validators.required,
                        phoneNumberValidator(),
                    ]),
                });
            });
        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
            });
    }

    ngOnDestroy(): void {
        // Emit to destroy$ to close all streams
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Open confirmation modal (the Save button is disabled while invalid;
    // touching everything surfaces errors if it's ever reached invalid).
    openConfirmationModal(): void {
        if (!this.userInformationForm.valid) {
            this.userInformationForm.markAllAsTouched();
            return;
        }
        this.confirmOpen = true;
    }

    /** Same translated copy the legacy confirmation dialog showed. */
    get confirmTitle(): string {
        return this.translate.instant(
            'UserInformation.updateUserInformationActionTitle',
        );
    }

    get confirmMessage(): string {
        return this.translate.instant(
            'UserInformation.updateUserInformationConfirmationMessage',
        );
    }

    onSaveConfirmed(): void {
        this.confirmOpen = false;
        this.submitForm();
    }

    getHelperText(field: string): string | null {
        const control: AbstractControl<any, any> | null =
            this.userInformationForm.get(field);

        // If no control found, return empty
        if (!control) {
            return null;
        }

        const requiredValidation: boolean =
            control.hasError('required') && control?.touched;
        const maxLengthValidation: boolean =
            control.hasError('maxlength') && control?.touched;
        const patternValidation: boolean =
            control.hasError('pattern') && control?.touched;
        const phoneNumberValidation: boolean =
            control.hasError('phoneNumber') && control?.touched;
        const emailValidation: boolean =
            control.hasError('email') && control?.touched;
        const usernameExistsValidation: boolean =
            control.hasError('usernameTaken') && control?.touched;
        switch (field) {
            case 'email':
                if (requiredValidation) {
                    return 'Email is required';
                }
                if (maxLengthValidation) {
                    return `Email can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (emailValidation) {
                    return `Email is invalid`;
                }
                break;
            case 'username':
                if (requiredValidation) {
                    return 'Username is required';
                }
                if (maxLengthValidation) {
                    return `Username can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (usernameExistsValidation) {
                    return 'Username exists';
                }
                if (patternValidation) {
                    return 'Username can have only alphabets and numbers';
                }
                break;
            case 'firstname':
                if (requiredValidation) {
                    return 'First Name is required';
                }
                if (maxLengthValidation) {
                    return `First Name can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (patternValidation) {
                    return 'First name can have only alphabets, no leading or trailing spaces allowed';
                }
                break;
            case 'lastname':
                if (requiredValidation) {
                    return 'Last Name is required';
                }
                if (maxLengthValidation) {
                    return `Last Name can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (patternValidation) {
                    return 'Last name can have only alphabets, no leading or trailing spaces allowed';
                }
                break;
            case 'phoneNumber':
                if (requiredValidation) {
                    return 'Phone number is required';
                }
                if (phoneNumberValidation) {
                    return `Phone number is invalid`;
                }
                break;
            default:
                return null;
        }

        return null;
    }

    toggleEditMode(): void {
        this.isEditMode = !this.isEditMode;

        if (this.isEditMode) {
            this.loadUserInformation();
        }
    }

    loadUserInformation(): void {
        this.userInformationForm.patchValue({
            email: this.userEmail || this.currentUser.email,
            firstname: this.currentUser.firstname,
            lastname: this.currentUser.lastname,
            phoneNumber: this.currentUser.accountTelephone?.number,
        });

        this.userInformationForm
            .get('username')
            ?.patchValue(this.currentUser.username, { emitEvent: false });
    }

    submitForm(): void {
        this.isLoading = true;
        if (this.userInformationForm.valid) {
            const userInformationData: PutUserInformationRequest = {
                firstname: this.userInformationForm.value.firstname,
                lastname: this.userInformationForm.value.lastname,
                accountTelephone: {
                    id: this.currentUser.accountTelephone!.id!,
                    number: prependCanadaAndUSCountryCode(
                        this.userInformationForm.value.phoneNumber,
                    ),
                    extension: this.currentUser.accountTelephone!.extension!,
                    countryCode:
                        this.currentUser.accountTelephone!.countryCode!,
                    name: this.currentUser.accountTelephone!.name!,
                },
                email: this.userInformationForm.value.email,
                username: this.userInformationForm.value.username,
            };
            this.profilesService
                .updateUserInformation(userInformationData)
                .subscribe({
                    next: () => {
                        const successMessage: string = this.translate.instant(
                            'UserInformation.updateUserInformationSuccessful',
                        );

                        // Use RxJS timer to delay the subscription
                        timer(1000).subscribe(() => {
                            this.snackbarService.success(successMessage);
                            this.profileService.updateUserStore();
                            this.isEditMode = false;
                            this.isLoading = false;
                        });
                    },
                    error: () => {
                        this.isEditMode = false;
                        this.isLoading = false;
                        this.snackbarService.error(
                            this.translate.instant(
                                'UserInformation.updateUserInformationError',
                            ),
                        );
                    },
                });
        }
    }

    onVerifyPhoneNumber(): void {
        this.verifyPhoneNumber = !this.verifyPhoneNumber;
        this.sendOTP = !this.sendOTP;
    }

    onTelephoneVerified(isPhoneVerified: boolean): void {
        this.isTelephoneVerified = isPhoneVerified;
        this.onVerifyPhoneNumber();
    }
}
